import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../Redux/store";
import { Order } from "../../../Types/Order";
import { OrderDetails } from "../../../Types/OrderDetail";

interface FetchOrderArgs {
    lookup?: string;
    page?: number;
    sortBy?: 'id' | 'dateOrder' | 'dateRequired' | null;
    sortDirection?: 'asc' | 'desc' | null;
    writerId?: string | null;
    completedOnly?: string;
};

export interface OrderResponse {
    orders: Order[];
    page: number;
    totalOrders: number;
    openOrders: number;
    completedOrders: number;
    openAmount: number;
    completedAmount: number;
    totalPages: number;
};

export const getOrders = createAsyncThunk<
    OrderResponse, 
    FetchOrderArgs, 
    { state: RootState }
    >('orders/getOrders',
    async(args,{ getState, rejectWithValue }) => {
        try {
            const token = getState().auth.token
            const params = new URLSearchParams();

            params.append('page', String(args?.page ?? 1));

            if (args?.sortBy) params.append('sortBy', args.sortBy);
            if (args?.sortDirection) params.append('sortDirection', args.sortDirection);
            if (args?.lookup) params.append('lookup', args.lookup);
            if (args?.writerId) params.append('writerId', args.writerId);
            if (args?.completedOnly) params.append('completedOnly', args.completedOnly);
            
            const baseUrl = `https://customer.hirsch.com/order/v2?${params.toString()}`

            const response = await fetch(baseUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                }
            })
            const json = await response.json()
            
            if (!json.ack) {
                return rejectWithValue(json.errorMessage);
            }
            console.log('ORDERS:', json);
            return { 
                orders: json.orders, 
                page: args?.page ?? 1,
                totalOrders: json.totalOrders.total,
                openOrders: json.totalOrders.uncompleted,
                completedOrders: json.totalOrders.completed,
                totalPages: json.totalPages,
                openAmount: json.totalAmounts.uncompleted,
                completedAmount: json.totalAmounts.completed,
            }
        } catch (err: any) {
            console.error('getOrders Error:', err)
            return rejectWithValue(`GetOrders Error: ${err}`)
        }
    }
)

export const getOrderDetails = createAsyncThunk<
    any[], 
    string, 
    { state: RootState }
    >('orders/getOrderDetails',
    async (key, { getState, rejectWithValue }) => {
        try{
            const token = getState().auth.token
            const orderUrl = 'https://customer.hirsch.com/order/v1/details'
            const response = await fetch(`${orderUrl}?order=${key}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                }
            })
            const json = await response.json()

            if (!json.ack) {
                return rejectWithValue(json.errorMessage);
            }
            // console.log(json.order.products[0].details)
            return json.order
        } catch (err: any) {
            return rejectWithValue(`GetOrderDetails Error: ${err}`)
        }
    }
)

interface OrderState {
    ordersArr: Order[];
    orderQueryArr: Order[];
    orderDetailArr: OrderDetails[];
    page: number;
    hasMore: boolean;
    currentRequestPage: number;
    totalOrders: number;
    openAmount: number;
    completedAmount: number;
    completedOrders: number;
    openOrders: number;
    loading: boolean;
    error: string | undefined | boolean | null;
}

const initialState: OrderState = {
    ordersArr: [],
    orderQueryArr: [],
    orderDetailArr: [],
    page: 1,
    hasMore: true,
    currentRequestPage: 1,
    totalOrders: 0,
    openOrders: 0,
    completedOrders: 0,
    openAmount: 0,
    completedAmount: 0,
    loading: false,
    error: null,
}

const orderSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        setOrderError: (state, action) => {
            state.error = action.payload;
        },
        setDeleteOrder: (state, action) => {
            const id = action.payload

            state.ordersArr = state.ordersArr.filter(order => order.id !== id)

            if (state.orderQueryArr) {
                state.orderQueryArr = state.orderQueryArr.filter(order => order.id !== id)
            }
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(getOrders.pending, (state, action) => {
            state.loading = true;
            state.currentRequestPage = action.meta.arg.page ?? 1;
            state.error = false;
        })
        .addCase(getOrders.fulfilled, (state, action) => {
            state.loading = false;
            
            const lookup = action.meta.arg?.lookup;
            
            state.currentRequestPage = action.payload.page;

            if (lookup) {
                if (action.payload.page === 1) {
                    state.orderQueryArr = action.payload.orders;
                    state.error = false;
                } else {
                    state.orderQueryArr.push(...action.payload.orders)
                }
            };


            if (action.payload.page === 1) {
                state.ordersArr = action.payload.orders;
            } else {
                state.ordersArr.push(...action.payload.orders);
            }
            
            state.page = action.payload.page;
            state.totalOrders = action.payload.totalOrders;
            state.openOrders = action.payload.openOrders;
            state.completedOrders = action.payload.completedOrders;
            state.openAmount = action.payload.openAmount;
            state.completedAmount = action.payload.completedAmount;
            state.hasMore = action.payload.page < action.payload.totalPages;
            state.error = false;
        })
        .addCase(getOrders.rejected, (state, action) => {
            state.loading = false
            state.error = action.error ? action.error.message : 'Failed to Fetch';

        })
        .addCase(getOrderDetails.pending, (state) => {
            state.loading = true;
            state.error = false;
        })
        .addCase(getOrderDetails.fulfilled, (state, action) => {
            state.loading = false;
            state.orderDetailArr = action.payload;
            state.error = false;
        })
        .addCase(getOrderDetails.rejected, (state, action) => {
            state.loading = false
            state.error = action.error ? action.error.message : 'Failed to Fetch';

        })
    }
})

export const ordersReducer = orderSlice.reducer;

export const {
    setOrderError,
    setDeleteOrder,
} = orderSlice.actions;

export const getOrderList = (state: RootState) => {
    return state.orders.ordersArr;
}
export const getOrderQueryList = (state: RootState) =>
    state.orders.orderQueryArr;
export const getOrdersPage = (state: RootState) =>
    state.orders.page;
export const getHasMoreOrders = (state: RootState) =>
    state.orders.hasMore;
export const getOrdersLoading = (state: RootState) =>
    state.orders.loading;
export const getOrdersRequestPage = (state: RootState) =>
    state.orders.currentRequestPage;
export const getTotalOrders = (state: RootState) =>
    state.orders.totalOrders;

export const getOrderDetail = (state: RootState) => {
    return state.orders.orderDetailArr;
}